import React from "react";
import styled, { css, keyframes } from "styled-components";
import { C, SEMI } from "../Styles";

const StepIndicator = ({ steps, current }) => (
  <Wrapper aria-label="Booking progress">
    {steps.map((label, i) => {
      const done = i < current;
      const active = i === current;
      const upcoming = !done && !active;
      return (
        <React.Fragment key={i}>
          <StepCell>
            <Circle
              $done={done}
              $active={active}
              role="img"
              aria-label={
                done ? "Completed" : active ? "Current step" : "Upcoming"
              }
            >
              {done && <Check />}
              {active && <span style={{ lineHeight: 1 }}>{i + 1}</span>}
              {upcoming && <UpcomingDot $show />}
            </Circle>
            <Label $done={done} $active={active}>
              {label}
            </Label>
            {active && (
              <ActivePill>
                <PillDot />
                In progress
              </ActivePill>
            )}
          </StepCell>
          {i < steps.length - 1 && (
            <ConnectorWrap>
              <ConnectorFill $done={done} />
            </ConnectorWrap>
          )}
        </React.Fragment>
      );
    })}
  </Wrapper>
);

export default StepIndicator;

const pulseTeal = keyframes`
  0%,100% { box-shadow: 0 0 0 0   rgba(0,131,140,0.38); }
  55%      { box-shadow: 0 0 0 10px rgba(0,131,140,0);   }
`;

const popIn = keyframes`
  0%   { transform: scale(0.5); opacity: 0; }
  70%  { transform: scale(1.1); }
  100% { transform: scale(1);   opacity: 1; }
`;

const fadeLabel = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Wrapper = styled.nav`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 28px 16px 32px;
  overflow-x: auto;
  scrollbar-width: none;
  gap: 0;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const StepCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  min-width: 64px;
`;

const Circle = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${SEMI};
  font-weight: 700;
  font-size: 13px;
  position: relative;
  z-index: 1;
  transition: all 0.35s ease;
  letter-spacing: -0.01em;
  ${(p) =>
    p.$done &&
    css`
      background: ${C.teal};
      color: ${C.white};
      box-shadow: 0 4px 16px rgba(0, 131, 140, 0.3);
      animation: ${popIn} 0.35s ease both;
    `}
  ${(p) =>
    p.$active &&
    css`
      background: ${C.navy};
      color: ${C.white};
      box-shadow: 0 4px 20px rgba(41, 52, 97, 0.32);
      animation: ${pulseTeal} 2.4s ease infinite;
    `}
  ${(p) =>
    !p.$done &&
    !p.$active &&
    css`
      background: ${C.white};
      color: ${C.muted};
      border: 1.5px solid ${C.line};
      box-shadow: 0 1px 4px rgba(41, 52, 97, 0.06);
    `}
`;

const UpcomingDot = styled.div`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${C.mutedLight};
  ${(p) =>
    !p.$show &&
    css`
      display: none;
    `}
`;

const Check = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path
      d="M2.5 7L5.5 10.5L11.5 3.5"
      stroke="white"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Label = styled.span`
  font-family: ${SEMI};
  font-size: 9.5px;
  font-weight: ${(p) => (p.$active ? 700 : 500)};
  color: ${(p) => (p.$done ? C.teal : p.$active ? C.navy : C.muted)};
  text-transform: uppercase;
  letter-spacing: 0.09em;
  white-space: nowrap;
  transition: color 0.3s ease;
  ${(p) =>
    p.$active &&
    css`
      animation: ${fadeLabel} 0.3s ease both;
    `}
`;

const ActivePill = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 2px 9px;
  border-radius: 99px;
  background: ${C.tealAlpha};
  border: 1px solid ${C.tealLine};
  font-family: ${SEMI};
  font-size: 9px;
  font-weight: 700;
  color: ${C.teal};
  letter-spacing: 0.07em;
  text-transform: uppercase;
  margin-top: -4px;
  animation: ${fadeLabel} 0.3s ease 0.1s both;
`;

const PillDot = styled.div`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${C.teal};
  flex-shrink: 0;
`;

const ConnectorWrap = styled.div`
  flex-shrink: 0;
  margin-bottom: 28px;
  width: clamp(24px, 5vw, 52px);
  height: 2px;
  border-radius: 99px;
  background: ${C.line};
  position: relative;
  overflow: hidden;
`;

const ConnectorFill = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 99px;
  background: ${C.teal};
  transform-origin: left;
  transform: scaleX(${(p) => (p.$done ? 1 : 0)});
  transition: transform 0.5s ease ${(p) => (p.$done ? "0s" : "0.3s")};
`;
